import {Injectable} from "@angular/core";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {UmbrellaValidators} from "../../../umbrella-common/validation/umbrella-validators";
import {Order, Product, Standard} from "../../../store/state/order.state";
import {AppState} from "../../../store/state/app.state";
import {Store} from "@ngrx/store";
import {OrderActions} from "../../../store/actions/order.actions";
import {FormService} from "../../../umbrella-common/services/form.service";
import {BaseOrderFormService} from "../common/base-order-form.service";
import {TranslateService} from "@ngx-translate/core";
import {takeWhile} from "rxjs/operators";

@Injectable()
export class CurrentCertificationBodyQuestionsFormService extends FormService implements BaseOrderFormService {

	private controlNamesInOrder: string[] = [];

	constructor(private fb: FormBuilder, private store: Store<AppState>, translate: TranslateService) {
		super(translate);
	}

	protected buildFormFor(formKey: string, [product, certificationBodies]: [Product, string[]]) {
		this.initControlNamesInOrder(product);
		const form = new FormGroup({});

		form.addControl('hasDekraCertificate' + formKey, this.fb.control(product.standard.hasDekraCertificate, [UmbrellaValidators.radiobuttonRequired()]));
		form.addControl('lastAuditEvent' + formKey, this.fb.control(product.transferQuestions ? product.transferQuestions.lastAuditEvent : null, [UmbrellaValidators.radiobuttonRequired(), UmbrellaValidators.nonEmpty]));

		if (!product.standard.hasDekraCertificate) {
			const value = product.standard.customCb ? certificationBodies[certificationBodies.length - 1] : product.standard.otherCbName;
			form.addControl('otherCbName' + formKey, this.fb.control(value, [Validators.required, UmbrellaValidators.nonEmpty]));
		}

		if (product.standard.customCb) {
			form.addControl('customCbName' + formKey, this.fb.control(product.standard.otherCbName, [Validators.required, UmbrellaValidators.textLengthBetween(2, 100)]));
		}

		this.initFormControls(product.standard, form, certificationBodies);
		return form;
	}

	getControlNamesInOrder(): string[] {
		return this.controlNamesInOrder;
	}

	fillOrder(order: Order): Order {
		this.getAllFormKeys().filter(formKey => this.isSubFormInitialized(formKey)).forEach(formKey => {
			this.fillProductInfo(order.products.find(p => p.standard.name === formKey), formKey);
		});

		return order;
	}

	private fillProductInfo(product: Product, formKey: string) {
		product.standard.hasDekraCertificate = this.getValueFor('hasDekraCertificate', formKey);
		product.transferQuestions.lastAuditEvent = this.getValueFor('lastAuditEvent', formKey);
		const customCbName: boolean = this.subFormMap[formKey].controls['customCbName' + formKey] != null;
		product.standard.otherCbName = this.getValueOrNull(formKey, customCbName ? 'customCbName' : 'otherCbName');
		product.standard.customCb = customCbName;
	}

	private initFormControls(standard: Standard, form: FormGroup, certificationBodies: string[]) {
		form.controls['lastAuditEvent' + standard.name].valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(standard.name)))
			.subscribe(lastAuditEvent => this.store.dispatch(OrderActions.SET_LAST_AUDIT_EVENT({standardName: standard.name, lastAuditEvent})));

		form.controls['hasDekraCertificate' + standard.name].valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(standard.name)))
			.subscribe(hasDekraCertificate => {
				this.store.dispatch(OrderActions.SET_HAS_DEKRA_CERTIFICATE({standardName: standard.name, hasDekraCertificate}));
			});

		if (!standard.hasDekraCertificate) {
			this.initOtherCbControl(standard, form, certificationBodies);
		}

		if (standard.customCb) {
			this.initCustomCbControl(standard, form);
		}
	}

	private initOtherCbControl(standard: Standard, form: FormGroup, certificationBodies: string[]) {
		form.controls['otherCbName' + standard.name].valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(standard.name)))
			.subscribe(otherCbName => {
				if (otherCbName === certificationBodies[certificationBodies.length - 1]) {
					this.subFormMap[standard.name].addControl('customCbName' + standard.name, new FormControl(null, [Validators.required, UmbrellaValidators.textLengthBetween(2, 100)]));
					this.initCustomCbControl(standard, form);
					this.store.dispatch(OrderActions.SET_CB_NOT_ON_LIST({standardName: standard.name, notOnList: true}));
				} else {
					this.subFormMap[standard.name].removeControl('customCbName' + standard.name);
					this.store.dispatch(OrderActions.SET_CB_NOT_ON_LIST({standardName: standard.name, notOnList: false}));
					this.store.dispatch(OrderActions.SET_CURRENT_CB_NAME({standardName: standard.name, otherCbName}));
				}
			})
	}

	private initCustomCbControl(standard: Standard, form: FormGroup) {
		form.controls['customCbName' + standard.name].valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(standard.name)))
			.subscribe(customCbName => {
				this.store.dispatch(OrderActions.SET_CURRENT_CB_NAME({standardName: standard.name, otherCbName: customCbName}));
			});
	}

	private initControlNamesInOrder(product: Product) {
		const formKey: string = product.standard.name;
		this.controlNamesInOrder = this.controlNamesInOrder.concat(['hasDekraCertificate' + formKey, 'lastAuditEvent' + formKey, 'otherCbName' + formKey, 'customCbName' + formKey]);
	}
}
